import React, { useEffect, useState } from 'react';
import { FileText, Download, Calendar, ChevronLeft, ChevronRight, Lock, CheckCircle2, Bell, BellOff, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const PortalPayslips = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [downloading, setDownloading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [payrollStatus, setPayrollStatus] = useState({ finalized: false, finalized_at: null, notify_subscribed: false });
  const [togglingNotify, setTogglingNotify] = useState(false);
  const token = localStorage.getItem('portal_token');

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);

  // Whenever month/year changes, ask backend whether HR has finalized that
  // month's main payroll. Only finalized months get a working Download button.
  useEffect(() => {
    let mounted = true;
    setStatusLoading(true);
    axios.get(`${API}/api/employee-portal/payroll-status?year=${year}&month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (mounted) setPayrollStatus(r.data || { finalized: false }); })
      .catch(() => { if (mounted) setPayrollStatus({ finalized: false }); })
      .finally(() => { if (mounted) setStatusLoading(false); });
    return () => { mounted = false; };
  }, [year, month, token]);

  const toggleNotify = async () => {
    setTogglingNotify(true);
    try {
      if (payrollStatus.notify_subscribed) {
        await axios.delete(`${API}/api/employee-portal/payslip-notify/unsubscribe?year=${year}&month=${month}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPayrollStatus(s => ({ ...s, notify_subscribed: false }));
      } else {
        await axios.post(`${API}/api/employee-portal/payslip-notify/subscribe?year=${year}&month=${month}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPayrollStatus(s => ({ ...s, notify_subscribed: true }));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not update notification preference.');
    } finally {
      setTogglingNotify(false);
    }
  };

  const handleDownload = async () => {    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/employee-portal/my-payslip?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payslip_${MONTHS[month - 1]}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Backend returns 403 with a friendly detail when payroll isn't finalized.
      // We have to parse the Blob response to extract the detail text.
      let msg = 'Failed to download payslip. Please try again.';
      try {
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) msg = parsed.detail;
        } else if (e?.response?.data?.detail) {
          msg = e.response.data.detail;
        }
      } catch { /* fall back to generic message */ }
      alert(msg);
    } finally {
      setDownloading(false);
    }
  };

  const goPrev = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const goNext = () => {
    const maxYear = now.getFullYear();
    const maxMonth = now.getMonth() + 1;
    if (year === maxYear && month >= maxMonth) return;
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const isCurrentOrFuture = year === now.getFullYear() && month > now.getMonth() + 1;
  const canDownload = payrollStatus.finalized && !statusLoading;

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-payslips">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
          <FileText className="h-5 w-5 text-violet-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Payslips</h2>
          <p className="text-xs text-gray-400">Download your monthly payslips once HR finalizes payroll</p>
        </div>
      </div>

      {/* Month/Year Navigator */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <button onClick={goPrev} className="p-2 rounded-lg hover:bg-gray-100 transition" data-testid="payslip-prev">
              <ChevronLeft className="h-5 w-5 text-gray-500" />
            </button>
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">{MONTHS[month - 1]}</p>
              <p className="text-sm text-gray-400">{year}</p>
            </div>
            <button onClick={goNext} className="p-2 rounded-lg hover:bg-gray-100 transition" data-testid="payslip-next"
              disabled={isCurrentOrFuture}>
              <ChevronRight className={`h-5 w-5 ${isCurrentOrFuture ? 'text-gray-200' : 'text-gray-500'}`} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Month</label>
              <Select value={String(month)} onValueChange={v => setMonth(Number(v))}>
                <SelectTrigger data-testid="payslip-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Year</label>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger data-testid="payslip-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status banner */}
          {statusLoading ? (
            <div
              className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 mb-3 flex items-center gap-2 text-xs text-gray-500"
              data-testid="payslip-status-loading"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-gray-300 animate-pulse"></span>
              Checking payroll status…
            </div>
          ) : payrollStatus.finalized ? (
            <div
              className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 mb-3 flex items-start gap-2"
              data-testid="payslip-status-finalized"
            >
              <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-emerald-800">Payroll finalized</p>
                <p className="text-emerald-700">
                  Your {MONTHS[month - 1]} {year} payslip is ready to download.
                </p>
              </div>
            </div>
          ) : (
            <div
              className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 mb-3"
              data-testid="payslip-status-not-finalized"
            >
              <div className="flex items-start gap-2">
                <Lock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs flex-1">
                  <p className="font-semibold text-amber-800">Not available yet</p>
                  <p className="text-amber-700">
                    {MONTHS[month - 1]} {year} payroll has not been finalized by HR.
                    You will be able to download your payslip here as soon as it's closed for the month.
                  </p>
                </div>
              </div>
              {!isCurrentOrFuture && (
                <button
                  type="button"
                  onClick={toggleNotify}
                  disabled={togglingNotify}
                  data-testid="notify-when-ready-toggle"
                  className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-medium rounded-md py-1.5 transition border ${
                    payrollStatus.notify_subscribed
                      ? 'bg-amber-200 border-amber-300 text-amber-900 hover:bg-amber-300'
                      : 'bg-white border-amber-300 text-amber-800 hover:bg-amber-100'
                  }`}
                >
                  {togglingNotify ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : payrollStatus.notify_subscribed ? (
                    <><BellOff className="h-3.5 w-3.5" /> Stop notifying me</>
                  ) : (
                    <><Bell className="h-3.5 w-3.5" /> Notify me when ready</>
                  )}
                </button>
              )}
            </div>
          )}

          <Button
            className="w-full py-5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handleDownload}
            disabled={downloading || !canDownload}
            data-testid="download-payslip-btn"
          >
            {canDownload ? <Download className="h-5 w-5 mr-2" /> : <Lock className="h-5 w-5 mr-2" />}
            {downloading
              ? 'Generating…'
              : canDownload
                ? `Download ${MONTHS[month - 1]} ${year} Payslip`
                : 'Payslip locked'}
          </Button>
        </CardContent>
      </Card>

      <div className="text-center">
        <Badge variant="outline" className="text-[10px] text-gray-400">
          <Calendar className="h-3 w-3 mr-1" /> e-Payslip &middot; Signature is not required
        </Badge>
      </div>
    </div>
  );
};

export default PortalPayslips;
